import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
  Grid,
  Box
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useObserver } from 'mobx-react-lite';
import React, { Fragment, FC } from 'react';
import { ViewState } from '../../domain/models/ViewState';
import { LayoutVariant } from '../Core/CommonTypes';
import { useShop } from '../../stores/ShopStore';
import ViewStateContainer from '../Core/ViewStateContainer/ViewStateContainer';
import Thanks from '../OrderingPage/Thanks/Thanks';
import SubmitErrorMessage from '../OrderingPage/SubmitOnlineOrder/Submit/SubmitErrorMessage';
import AmountRow from '../OrderingPage/OrderDetail/AmountRow';

const useStyles = makeStyles(theme => ({
  paper: {
    height: 'calc(100% - 96px)',
    minWidth: 480
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  dialogContent: {},
  mobileDialogContent: {
    padding: theme.spacing(2),
    paddingTop: 0
  },
  container: {
    flexWrap: 'nowrap'
  },
  message: {
    margin: theme.spacing(2),
    textAlign: 'center'
  },
  submit: {
    margin: theme.spacing(2)
  },
  orderSummary: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(4),
    padding: theme.spacing(1),
    minWidth: 320
  }
}));

interface SubmitShopOrderProps {
  variant?: LayoutVariant;
  onClose?: () => void;
}

const SubmitShopOrder: FC<SubmitShopOrderProps> = ({ onClose, variant }) => {
  const classes = useStyles();
  const shopStore = useShop();

  const order = shopStore.order;

  const handleClose = () => {
    if (onClose !== undefined) {
      onClose();
    }
  };

  return useObserver(() => (
    <Dialog
      onClose={handleClose}
      open
      scroll="paper"
      fullWidth={variant === LayoutVariant.laptop}
      fullScreen={variant === LayoutVariant.mobile}
      maxWidth="md"
      disableBackdropClick={shopStore.submitState === ViewState.LOADING}
      classes={variant === LayoutVariant.mobile ? {} : { paper: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.title}>
          Submit your order
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className={classes.close}
          disabled={shopStore.submitState === ViewState.LOADING}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        classes={{
          root:
            variant === LayoutVariant.mobile
              ? classes.mobileDialogContent
              : classes.dialogContent
        }}
      >
        {shopStore.submitState === ViewState.OK ? (
          <Thanks isSendEmail={true} />
        ) : (
          <Fragment>
            <Grid
              container
              className={classes.container}
              direction="column"
              alignContent="center"
              alignItems="center"
              justify="center"
            >
              <ViewStateContainer
                state={shopStore.submitState}
                errorComponent={
                  <Box>
                    <Typography
                      className={classes.message}
                      variant="h6"
                      color="textSecondary"
                      gutterBottom
                    >
                      There was error submitting your order.
                    </Typography>
                    <SubmitErrorMessage
                      message={shopStore.submitErrorMessage}
                    />
                  </Box>
                }
              >
                <Box>
                  <Typography
                    className={classes.message}
                    variant="h6"
                    color="textSecondary"
                    gutterBottom
                  >
                    We got all necessary detail.
                  </Typography>
                </Box>
              </ViewStateContainer>
              <Grid item container direction="row" justify="center">
                <Button
                  className={classes.submit}
                  variant="contained"
                  size="large"
                  color="primary"
                  aria-label="pay submit order"
                  onClick={() => shopStore.submitCurrentOrder()}
                  disabled={
                    shopStore.submitToken.length === 0 ||
                    shopStore.submitState === ViewState.LOADING
                  }
                >
                  SUBMIT YOUR ORDER
                </Button>
              </Grid>
              <Box className={classes.orderSummary}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  YOUR ORDER SUMMARY
                </Typography>
                <Box>
                  <AmountRow name="TOTAL" amount={order.total} variant="h5" />
                </Box>
              </Box>
            </Grid>
          </Fragment>
        )}
      </DialogContent>
    </Dialog>
  ));
};

export default SubmitShopOrder;
